import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ZAxis } from 'recharts'

import { resolveRowKeyForDataKey } from '@/utils/table-cell-value'

interface ScatterPlotComponentProps {
  data: Array<Record<string, any>>
  xAxisKey: string
  yAxisKey: string
  zAxisKey?: string // Opcional: tamaño de los puntos
  groupKey?: string // Opcional: agrupar por categoría
  title?: string
  showGrid?: boolean
  colors?: string[]
  loading?: boolean
}

// Paleta de colores por defecto
const DEFAULT_COLORS = [
  '#10b981', // Verde
  '#3b82f6', // Azul
  '#f59e0b', // Naranja
  '#ef4444', // Rojo
  '#8b5cf6', // Morado
  '#14b8a6', // Teal
]

export function ScatterPlotComponent({
  data,
  xAxisKey,
  yAxisKey,
  zAxisKey,
  groupKey,
  title,
  showGrid = true,
  colors = DEFAULT_COLORS,
  loading = false,
}: ScatterPlotComponentProps) {
  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center text-gray-500">
          No hay datos disponibles
        </div>
      </div>
    )
  }

  // Alias SQL / claves "tabla.campo" → claves reales en cada fila (p. ej. biomasa_aerea)
  const sample = data[0] as Record<string, unknown>
  const resolvedXKey = resolveRowKeyForDataKey(sample, xAxisKey)
  const resolvedYKey = resolveRowKeyForDataKey(sample, yAxisKey)
  const resolvedZKey = zAxisKey
    ? resolveRowKeyForDataKey(sample, zAxisKey)
    : undefined
  const resolvedGroupKey = groupKey
    ? resolveRowKeyForDataKey(sample, groupKey)
    : undefined

  // Transformar datos para asegurar tipos correctos
  const chartData = data.map((item) => {
    const rawX = item[resolvedXKey]
    const rawY = item[resolvedYKey]
    const rawZ = resolvedZKey ? item[resolvedZKey] : undefined

    const xNum = Number(rawX)
    const yNum = Number(rawY)

    let zNum = 1
    if (resolvedZKey && rawZ !== undefined && rawZ !== null) {
      const n = Number(rawZ)
      zNum = Number.isFinite(n) ? n : 1
    }

    const nameFromZ =
      typeof rawZ === 'string' || typeof rawZ === 'number'
        ? String(rawZ)
        : undefined

    return {
      x: Number.isFinite(xNum) ? xNum : 0,
      y: Number.isFinite(yNum) ? yNum : 0,
      z: zNum,
      /** Valor crudo de Z para tooltips cuando Z es categórico (p. ej. municipio) */
      zRaw: rawZ,
      group: resolvedGroupKey
        ? String(item[resolvedGroupKey] ?? '')
        : 'default',
      name:
        item.name ||
        nameFromZ ||
        (rawX !== undefined && rawX !== null ? String(rawX) : '') ||
        (rawY !== undefined && rawY !== null ? String(rawY) : '') ||
        'Sin nombre',
      ...item,
    }
  })

  // Si hay groupKey, agrupar datos
  let scatterGroups: Array<{ name: string; data: any[] }> = []

  if (groupKey) {
    const groups = new Map<string, any[]>()

    chartData.forEach((item) => {
      const group = item.group
      if (!groups.has(group)) {
        groups.set(group, [])
      }
      groups.get(group)!.push(item)
    })

    scatterGroups = Array.from(groups.entries()).map(([name, data]) => ({
      name,
      data,
    }))
  } else {
    scatterGroups = [{ name: 'Datos', data: chartData }]
  }

  // Custom tooltip (usa claves resueltas para etiquetas legibles)
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload

      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900 mb-2">{d.name}</p>
          <p className="text-sm text-gray-600">
            {resolvedXKey}:{' '}
            <span className="font-medium">{d.x.toLocaleString('es-ES')}</span>
          </p>
          <p className="text-sm text-gray-600">
            {resolvedYKey}:{' '}
            <span className="font-medium">{d.y.toLocaleString('es-ES')}</span>
          </p>
          {zAxisKey && (
            <p className="text-sm text-gray-600">
              {resolvedZKey}:{' '}
              <span className="font-medium">
                {d.zRaw !== undefined && d.zRaw !== null
                  ? typeof d.zRaw === 'number'
                    ? d.zRaw.toLocaleString('es-ES')
                    : String(d.zRaw)
                  : d.z.toLocaleString('es-ES')}
              </span>
            </p>
          )}
          {groupKey && (
            <p className="text-sm text-gray-600">
              {resolvedGroupKey ?? groupKey}:{' '}
              <span className="font-medium">{d.group}</span>
            </p>
          )}
        </div>
      )
    }
    return null
  }

  // Rango del eje Z (tamaño de puntos)
  const zRange = zAxisKey ? [50, 400] : [100, 100]

  return (
    <div className="bg-white border rounded-lg p-6">
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}

      <ResponsiveContainer width="100%" height={400}>
        <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
          <XAxis
            type="number"
            dataKey="x"
            name={resolvedXKey}
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            tickFormatter={(value) => value.toLocaleString('es-ES')}
            label={{
              value: resolvedXKey,
              position: 'insideBottom',
              offset: -10,
              style: { fontSize: '12px' },
            }}
          />
          <YAxis
            type="number"
            dataKey="y"
            name={resolvedYKey}
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            tickFormatter={(value) => value.toLocaleString('es-ES')}
            label={{
              value: resolvedYKey,
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: '12px' },
            }}
          />
          {zAxisKey && resolvedZKey && (
            <ZAxis type="number" dataKey="z" range={zRange} name={resolvedZKey} />
          )}

          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />

          {scatterGroups.length > 1 && (
            <Legend
              wrapperStyle={{ fontSize: '12px' }}
              iconType="circle"
            />
          )}

          {/* Renderizar scatter por grupo */}
          {scatterGroups.map((group, index) => (
            <Scatter
              key={group.name}
              name={group.name}
              data={group.data}
              fill={colors[index % colors.length]}
              fillOpacity={0.7}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>

      {/* Info adicional */}
      <div className="mt-4 text-xs text-gray-500 text-center">
        {zAxisKey && resolvedZKey
          ? sample[resolvedZKey] !== undefined &&
            typeof sample[resolvedZKey] === 'number'
            ? `El tamaño de los puntos representa ${resolvedZKey}`
            : `Etiqueta por punto: ${resolvedZKey} (tamaño fijo si el campo no es numérico)`
          : 'Gráfico de dispersión: relación entre dos variables'}
      </div>
    </div>
  )
}
